/**
 * Where a failed delivery's Twilio error code puts the fault.
 *
 * - `recipient`: the number itself cannot receive texts.
 * - `unsupported`: the number takes texts but not this kind of message, as when
 *   an MMS reaches a handset or carrier without MMS.
 * - `transient`: the handset or carrier could not be reached just then. A phone
 *   that is switched off and a carrier that stopped accepting one of our senders
 *   both land here, so only a pattern across recipients tells them apart.
 * - `sender`: the fault is ours — registration, filtering, throughput, queueing.
 *   Every code not in {@link SMS_ERROR_CODES} lands here, so an unfamiliar one
 *   is reported rather than absorbed.
 */
export type SmsErrorClass = 'recipient' | 'unsupported' | 'transient' | 'sender';

/** What one Twilio error code blames, and what an operator reads for it. */
export interface ISmsErrorCode {
  errorClass: SmsErrorClass;
  label: string;
}

/** The Twilio error codes a delivery receipt carries that we can name. */
export const SMS_ERROR_CODES: Readonly<Partial<Record<number, ISmsErrorCode>>> = {
  // Ours: a send reaching a number that said STOP means our consent record missed it.
  21610: { errorClass: 'sender', label: 'Recipient has opted out (STOP)' },
  30003: { errorClass: 'transient', label: 'Unreachable handset' },
  30004: { errorClass: 'transient', label: 'Message blocked' },
  30005: { errorClass: 'recipient', label: 'Unknown destination handset' },
  30006: { errorClass: 'recipient', label: 'Landline or unreachable carrier' },
  30007: { errorClass: 'sender', label: 'Filtered by the carrier' },
  30008: { errorClass: 'transient', label: 'Unknown error' },
  30011: { errorClass: 'unsupported', label: 'MMS not supported by the destination' },
  30017: { errorClass: 'transient', label: 'Carrier network congestion' },
  30024: { errorClass: 'sender', label: 'Sender not provisioned by the carrier' },
  30032: { errorClass: 'sender', label: 'Toll-free sender not verified' },
  30034: { errorClass: 'sender', label: 'Sender not registered for A2P 10DLC' },
  30036: { errorClass: 'sender', label: "Expired in Twilio's queue" },
  30410: { errorClass: 'transient', label: 'Provider timeout' },
};

/** Classifies a Twilio error code, as the message resource (number) or a status callback (string) carries it. */
export function classifySmsError(code: number | string): SmsErrorClass {
  return SMS_ERROR_CODES[Number(code)]?.errorClass ?? 'sender';
}

/** What an operator reads for a Twilio error code, or `null` for one {@link SMS_ERROR_CODES} does not name. */
export function describeSmsError(code: number | string): string | null {
  return SMS_ERROR_CODES[Number(code)]?.label ?? null;
}
